// «Цитувати виділене» — quote a specific part of a post or comment.
//
// Select any text inside a post body (.text-body-type-post) or a comment body
// (.text-body-type-comment) → a floating «💬 Цитувати» button appears next to
// the selection. Clicking it drops the selected text as a Markdown blockquote
// into the comment composer, Telegram / forum style:
//   • quoting a COMMENT  → opens the reply form under that comment, prefilled
//     with the quote + an @mention of its author;
//   • quoting the POST   → drops the quote into the main comment box at the
//     bottom.
//
// This removes the «describe which moment you mean, then ask» dance — you point
// at the exact fragment and your question lands right under it.
//
// Vanilla + delegated on `document` so it survives Vue's #app re-mount.

const BUTTON_ID = "quote-tooltip";

function getButton() {
    let el = document.getElementById(BUTTON_ID);
    if (el) return el;
    el = document.createElement("button");
    el.id = BUTTON_ID;
    el.type = "button";
    el.className = "quote-tooltip";
    el.textContent = "💬 Цитувати";
    el.style.display = "none";
    document.body.appendChild(el);
    return el;
}

// Each selected line → a Markdown blockquote line. Blank lines stay blank-
// quoted so paragraph breaks inside the quote survive markdown rendering.
function toBlockquote(text) {
    return text
        .replace(/\r/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .split("\n")
        .map((line) => "> " + line)
        .join("\n");
}

// Only offer quoting where the viewer can actually post a reply/comment.
function canComment() {
    return !!document.querySelector(".post-comments-form, .reply-form-form, #comment-reply-form");
}

function readSelection() {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;

    const text = sel.toString().trim();
    if (!text) return null;

    const range = sel.getRangeAt(0);
    let node = range.commonAncestorContainer;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
    const body = node && node.closest ? node.closest(".text-body") : null;
    if (!body) return null;

    const isComment = body.classList.contains("text-body-type-comment");
    const isPost = body.classList.contains("text-body-type-post");
    if (!isComment && !isPost) return null;

    return { text, range, body, isComment };
}

let current = null;

function hideButton() {
    const btn = document.getElementById(BUTTON_ID);
    if (btn) btn.style.display = "none";
    current = null;
}

function placeButton(btn, range) {
    const rects = range.getClientRects();
    const rect = rects.length ? rects[rects.length - 1] : range.getBoundingClientRect();
    if (!rect || (!rect.width && !rect.height)) return;

    btn.style.display = "block";
    const top = window.scrollY + rect.bottom + 8;
    let left = window.scrollX + rect.right - btn.offsetWidth;
    if (left < window.scrollX + 8) left = window.scrollX + 8;
    btn.style.top = Math.round(top) + "px";
    btn.style.left = Math.round(left) + "px";
}

function refresh() {
    const info = readSelection();
    if (!info || !canComment()) {
        hideButton();
        return;
    }
    current = info;
    placeButton(getButton(), info.range);
}

// ── Inserting the quote into the right editor ──────────────────────────────

function quoteComment(info, quote) {
    const container = info.body.closest("[id^='comment-']");
    if (!container) return false;
    const commentId = container.id.replace(/^comment-/, "");
    const authorEl = container.querySelector(".comment-header-author-name[data-author-slug]");
    const slug = authorEl ? authorEl.dataset.authorSlug : "";

    // Hand off to the shared reply-form (Vue root) — same path as «ответить»,
    // but with a quote payload the form prepends to the draft text.
    if (window.__clubApp && typeof window.__clubApp.showReplyForm === "function") {
        window.__clubApp.showReplyForm(commentId, slug, commentId, quote);
        return true;
    }
    return false;
}

function quotePost(quote) {
    const box = document.querySelector("#post-comments-form .comment-form-editor-container");
    const cmEl = document.querySelector("#post-comments-form .CodeMirror");
    if (!cmEl || !cmEl.CodeMirror) return false;

    const cm = cmEl.CodeMirror;
    let existing = cm.getValue();

    // If the box still shows the post's «example» comment template (cleared on
    // first focus elsewhere), wipe it so we don't quote on top of boilerplate.
    if (box && box.hasAttribute("data-clear-on-focus") && !box.dataset.tmplCleared) {
        box.dataset.tmplCleared = "1";
        existing = "";
    }

    cm.setValue(quote + "\n\n" + existing);
    cm.focus();
    cm.setCursor(cm.lineCount(), 0);

    const form = document.getElementById("post-comments-form");
    if (form) form.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
}

export function initQuoteSelection() {
    if (window.__quoteSelectionInit) return;
    window.__quoteSelectionInit = true;

    const btn = getButton();

    // Keep the selection alive through the click (mousedown would clear it).
    btn.addEventListener("mousedown", (e) => e.preventDefault());

    btn.addEventListener("click", (e) => {
        e.preventDefault();
        if (!current) return;
        const quote = toBlockquote(current.text);
        if (current.isComment) quoteComment(current, quote);
        else quotePost(quote);
        const sel = window.getSelection();
        if (sel) sel.removeAllRanges();
        hideButton();
    });

    document.addEventListener("mouseup", () => setTimeout(refresh, 0));
    document.addEventListener("keyup", (e) => {
        if (e.shiftKey || e.key === "Shift" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
            setTimeout(refresh, 0);
        }
    });
    document.addEventListener("mousedown", (e) => {
        if (e.target !== btn) hideButton();
    });
    document.addEventListener("scroll", hideButton, true);
}
